// src/hooks/useFeatureAccess.ts
//
// Reusable per-feature visibility check. This is the same resolution that
// AnalyticsHubPage does inline (tenant defaults + per-user overrides + catalog
// fallback), lifted into a hook so nested components — e.g. sub-sections
// *inside* a page like InventoryTab — can gate their own sub-features without
// prop-drilling a `canSee` function down through several component layers.
//
// It reuses the EXACT same React Query keys as AnalyticsHubPage, so when this
// mounts underneath the hub the three feature queries are already cached — no
// extra network round-trips.
//
// Precedence for a normal tenant user: per-user override → tenant DB row →
// catalog default (see isFeatureEnabled). Sysadmins and "show all" users
// (no roles) see everything, matching the page-level behaviour.

import { useQuery } from '@tanstack/react-query';
import { useAuthContext } from './useAuth';
import { adminApi } from '../services/adminApi';
import { hasAdminRole } from '../lib/sysadminRoles';
import { isFeatureEnabled, getCatalogDefault } from '../lib/featureCatalog';

export interface FeatureAccess {
  /** True when the feature is visible for the current tenant + user. */
  canSee: (feature: string) => boolean;
  /** The feature tables are still loading. */
  loading: boolean;
  /** One of the feature queries failed (canSee falls back to catalog defaults). */
  errored: boolean;
}

export function useFeatureAccess(): FeatureAccess {
  const { user, claims, activeTenant } = useAuthContext();
  const slug = activeTenant?.slug ?? claims?.tenant_slug ?? null;
  const userRoles: string[] = claims?.roles || [];
  const showAll = userRoles.length === 0;
  const isSysadmin = hasAdminRole(userRoles);

  // Same query keys + options as AnalyticsHubPage → shared cache, no refetch.
  const featuresQ = useQuery({
    queryKey: ['analytics-hub', 'features', slug],
    queryFn: () => adminApi.getAllFeatures(slug ?? undefined),
    enabled: !!slug && !showAll && !isSysadmin,
    refetchOnWindowFocus: false, retry: 0, staleTime: 60_000,
  });
  const effectiveQ = useQuery({
    queryKey: ['analytics-hub', 'effective', slug, user?.id],
    queryFn: () => adminApi.getEffectiveFeatures(user!.id, slug ?? undefined),
    enabled: !!slug && !!user?.id && !showAll && !isSysadmin,
    refetchOnWindowFocus: false, retry: 0, staleTime: 60_000,
  });
  const overridesQ = useQuery({
    queryKey: ['analytics-hub', 'overrides', slug, user?.id],
    queryFn: () => adminApi.getAllUserOverrides(user!.id, slug ?? undefined),
    enabled: !!slug && !!user?.id && !showAll && !isSysadmin,
    refetchOnWindowFocus: false, retry: 0, staleTime: 60_000,
  });

  const effectiveSet  = new Set(effectiveQ.data ?? []);
  const dbFeatureKeys = new Set((featuresQ.data ?? []).map((f) => f.featureKey));
  const userOverrides = new Map<string, boolean>(
    (overridesQ.data ?? []).map((o) => [o.featureKey, o.isEnabled]),
  );
  const loading = featuresQ.isLoading || effectiveQ.isLoading || overridesQ.isLoading;
  const errored = !!(featuresQ.error || effectiveQ.error || overridesQ.error);

  function canSee(feature: string): boolean {
    if (showAll || isSysadmin) return true;
    // While the tables load (or if a query errors), fall back to the catalog
    // default. Unlike the hub's tab gating — which hides tabs until it knows —
    // these in-page sub-features are cosmetic and default-ON, so honouring the
    // catalog default during the brief load avoids a flash-out/flash-in.
    if (loading || errored) return getCatalogDefault(feature);
    return isFeatureEnabled(feature, effectiveSet, dbFeatureKeys, userOverrides);
  }

  return { canSee, loading, errored };
}
